import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { UtilsService } from '../../shared/utils.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TagModel } from '../../models/tag.model';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-account-tags-edit',
  templateUrl: './account-tags-edit.component.html',
  styleUrls: ['./account-tags-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountTagsEditComponent implements OnInit {
  inProgress = false;

  // RBAC permission flags. Resolved once in ngOnInit() so the template does not
  // re-evaluate permission checks on every change detection cycle.
  canCreate: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  constructor(
    private utils: UtilsService,
    @Inject(MAT_DIALOG_DATA) public tag: TagModel,
    private dialogRef: MatDialogRef<AccountTagsEditComponent>,
    private api: ApiRequestService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_TAGS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_TAGS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_TAGS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    if (this.tag.id) {
      this.find(this.tag.id);
    }
  }

  onSubmit(form: NgForm) {
    // RBAC check: CREATE for new tags, VIEW+EDIT for existing tags
    if (!this.tag.id) {
      if (
        !this.rbac.userCanAccess(
          [
            RBACService.getPermission(
              RBACService.RBAC_F.F_ACCOUNT_TAGS,
              RBACService.RBAC_A.A_CREATE
            )
          ],
          true
        )
      ) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to create tags.'
        );
        return;
      }
    } else {
      if (
        !this.rbac.userCanAccess(
          [
            RBACService.getPermission(
              RBACService.RBAC_F.F_ACCOUNT_TAGS,
              RBACService.RBAC_A.A_EDIT
            )
          ],
          true
        )
      ) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to edit tags.'
        );
        return;
      }
    }

    // Check if the button was already pressed.
    if (this.inProgress) {
      return;
    }

    // Check form validation.
    if (!form.valid) {
      this.utils.showFormValidationError('Please enter all required fields.');
      return;
    }

    this.inProgress = true;

    !this.tag.id ? this.create() : this.update();
  }

  private find(tag_id: number) {
    this.api
      .makeRequest('get', `v2/tags/${tag_id}`)
      .then((response) => {
        this.tag = response;
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  onDelete() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_TAGS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to archive tags.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Tag',
      `Are you sure you want to archive "${this.tag.title}"?`,
      () => {
        this.api
          .makeRequest('delete', `v2/tags/${this.tag.id}`)
          .then((response) => {
            this.utils.showToast(`"${this.tag.title}" has been archived.`);
            this.dialogRef.close(true);
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  private update() {
    this.api
      .makeRequest('put', `v2/tags/${this.tag.id}`, this.tag)
      .then((response) => {
        this.tag = response.data;
        this.onSuccess(`Updated: "${response.data.title}"`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => (this.inProgress = false));
  }

  private create() {
    this.api
      .makeRequest('post', 'v2/tags', this.tag)
      .then((response) => {
        this.tag = response.data;
        this.onSuccess(`New Tags "${response.data.title}" created.`);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      })
      .finally(() => (this.inProgress = false));
  }

  private onSuccess(message: string) {
    this.utils.showToast(message);
    this.dialogRef.close(this.tag);
  }

  protected readonly RBACService = RBACService;
}
